import { FC, useEffect } from 'react'

import { DocumentFile, DocumentType, DocumentWithTypeFile } from 'modules/domain/types'
import { useTranslation } from 'react-i18next'
import { apiClient } from 'modules/utils/httpClient'
import { endpoints } from 'modules/endpoints'
import { uploadDocuments } from 'modules/domain/common/managers'
import { AFilesByTypes } from 'views/components/FilesByTypes/AFilesByTypes'
import { refetchFunc, useSingleEntity } from 'modules/domain/common/hooks'
import { Progress } from '@agro-club/agroclub-shared'
import { useAProgress } from 'hooks/useAProgress'

type Props = {
  setSummary?: (arg: number) => void
  carrierId: string
  documentsRefetch: refetchFunc
  documents: DocumentFile[]
  documentsProgress: Progress
}

export const CarrierDocuments: FC<Props> = ({
  carrierId,
  setSummary,
  documentsRefetch,
  documents,
  documentsProgress,
}) => {
  const { t } = useTranslation('logisticsKR')

  const [, documentTypes = []] = useSingleEntity<DocumentType[]>(endpoints.LogisticsKR.carrierDocumentsTypes(carrierId))

  useEffect(() => {
    if (documentsProgress === Progress.SUCCESS && documentTypes.length > 0 && setSummary) {
      setSummary(
        documentTypes.reduce((acc, type) => {
          const documentsOfType = documents.filter((document) => document.document_type === type.slug)
          return acc + documentsOfType.length
        }, 0),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents.length, documentTypes])

  const descriptions = {
    company_documents: t('carriers.accordion.descriptions.company_documents'),
    knd_1151162_form: t('carriers.accordion.descriptions.knd_1151162_form'),
    copy_report_efs1: t('carriers.accordion.descriptions.copy_report_efs1'),
  }

  const [uploadProgress, onUploadHandler] = useAProgress<DocumentWithTypeFile[]>(
    (documentType: DocumentType['slug'], files: File[]) => {
      const promises: Promise<DocumentWithTypeFile>[] = []
      for (const file of files) {
        promises.push(uploadDocuments(endpoints.LogisticsKR.carrierDocuments(carrierId), documentType, file))
      }
      return Promise.all(promises)
    },
    {
      eventName: 'uploadDocument',
      onSuccess: () => documentsRefetch(),
    },
  )

  const [removeProgress, removeDoc] = useAProgress(
    (documentId: string) => apiClient.delete(endpoints.LogisticsKR.carrierDocument(carrierId, documentId)),
    {
      eventName: 'deleteDocument',
      onSuccess: () => documentsRefetch(),
    },
  )

  return (
    <>
      <AFilesByTypes
        types={documentTypes}
        files={documents}
        uploadProgress={uploadProgress}
        onUpload={onUploadHandler}
        onDelete={removeDoc}
        deleteProgress={removeProgress}
        description={descriptions}
      />
    </>
  )
}
